import { useMemo, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Compass, Sparkles, Users, Euro, Heart, CalendarClock } from 'lucide-react';
import { usePost } from '@/lib/useData';
import { SectionCard } from '@/components/common/SectionCard';
import { CardSkeleton } from '@/components/common/Loading';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { fmtInt, fmtNum } from '@/lib/format';

const eur0 = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);

const CANALS = [
  { key: 'porte_a_porte', label: 'Porte-à-porte' },
  { key: 'sites_prives', label: 'Sites privés' },
  { key: 'rue', label: 'Rue' },
];

const scoreColor = (s) => (s >= 85 ? '#0E9F6E' : s >= 75 ? '#0B7A75' : s >= 65 ? '#F4A13A' : '#94A3B8');

const CRITS = [
  { key: 'revenu', label: 'Revenu', icon: Euro, color: 'hsl(var(--chart-2))' },
  { key: 'generosite', label: 'Générosité', icon: Heart, color: '#0E9F6E' },
  { key: 'age', label: 'Âge', icon: CalendarClock, color: 'hsl(var(--chart-1))' },
  { key: 'population', label: 'Population', icon: Users, color: 'hsl(var(--chart-3))' },
];

const CritBar = ({ value, color }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
    <div className="h-full rounded-full" style={{ width: `${Math.max(3, value)}%`, background: color }} />
  </div>
);

const ProspectMap = ({ cities, height = 540, selected, onSelect }) => {
  const radiusFor = (s) => 6 + Math.max(0, (s - 60)) / 40 * 16;
  return (
    <div className="relative overflow-hidden rounded-2xl border" style={{ height }} data-testid="prospect-map">
      <MapContainer center={[46.6, 2.4]} zoom={6} scrollWheelZoom style={{ height: '100%', width: '100%', background: 'var(--tenant-accent-050)' }} preferCanvas>
        <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" attribution="&copy; OpenStreetMap, &copy; CARTO" />
        {cities.map((c) => {
          const active = selected === c.city;
          const col = scoreColor(c.score);
          return (
            <CircleMarker
              key={`${c.city}-${c.dept}`}
              center={[c.lat, c.lng]}
              radius={radiusFor(c.score)}
              pathOptions={{ color: col, fillColor: col, fillOpacity: active ? 0.9 : 0.55, weight: active ? 3 : 1.4 }}
              eventHandlers={{ click: () => onSelect?.(c.city) }}
            >
              <Tooltip direction="top" offset={[0, -4]}>
                <span style={{ fontWeight: 600 }}>{c.city}</span> — score {c.score}
              </Tooltip>
              <Popup>
                <div style={{ minWidth: 180 }}>
                  <p style={{ margin: 0, fontWeight: 700, fontSize: 13 }}>{c.city} <span style={{ color: '#5B6472', fontWeight: 400 }}>({c.dept})</span></p>
                  <p style={{ margin: '2px 0 6px', fontSize: 12 }}>Score prospection : <b>{c.score}/100</b></p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 12 }}>
                    <div>{eur0(c.income)}<br /><span style={{ color: '#5B6472' }}>Revenu méd.</span></div>
                    <div>{fmtNum(c.age_med, 0)} ans<br /><span style={{ color: '#5B6472' }}>Âge méd.</span></div>
                    <div>{fmtInt(c.pop)}<br /><span style={{ color: '#5B6472' }}>Habitants</span></div>
                    <div>{c.gen_imp}%<br /><span style={{ color: '#5B6472' }}>Foyers imposés</span></div>
                  </div>
                </div>
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>
      <div className="pointer-events-none absolute bottom-3 left-3 z-[400] rounded-xl border bg-card/90 px-3 py-2 text-[11px] backdrop-blur">
        <p className="mb-1 font-semibold text-foreground">Score de prospection</p>
        <div className="flex items-center gap-2">
          {[['#0E9F6E', '≥85'], ['#0B7A75', '≥75'], ['#F4A13A', '≥65'], ['#94A3B8', '<65']].map(([c, l]) => (
            <span key={l} className="flex items-center gap-1 text-muted-foreground"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: c }} />{l}</span>
          ))}
        </div>
        <p className="mt-1 text-muted-foreground">Taille = score</p>
      </div>
    </div>
  );
};

const ProspectCard = ({ item, rank, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid="prospect-card"
    className={`w-full rounded-xl border bg-card p-3 text-left transition-[transform,border-color] duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-400)] ${selected ? 'border-[color:var(--tenant-accent-500)] ring-2 ring-[color:var(--tenant-accent-400)]/40' : 'hover:border-[color:var(--tenant-accent-300)]'}`}
  >
    <div className="flex items-start justify-between gap-2">
      <span className="flex min-w-0 items-center gap-2">
        <span className="text-xs font-semibold text-muted-foreground tabular">{rank}</span>
        <span className="min-w-0">
          <span className="block break-words text-sm font-semibold">{item.city}</span>
          <span className="block text-[11px] text-muted-foreground break-words">{item.dept_name} ({item.dept})</span>
        </span>
      </span>
      <span
        className="shrink-0 rounded-lg px-2 py-0.5 text-xs font-bold text-white"
        style={{ background: scoreColor(item.score) }}
      >
        {item.score}
      </span>
    </div>

    <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
      <div><p className="text-muted-foreground">Revenu méd.</p><p className="font-medium tabular break-words">{eur0(item.income)}</p></div>
      <div><p className="text-muted-foreground">Âge méd.</p><p className="font-medium tabular">{fmtNum(item.age_med, 0)} ans</p></div>
      <div><p className="text-muted-foreground">Habitants</p><p className="font-medium tabular">{fmtInt(item.pop)}</p></div>
    </div>

    <div className="mt-2.5 space-y-1.5">
      {CRITS.map((cr) => (
        <div key={cr.key} className="flex items-center gap-2">
          <cr.icon className="h-3 w-3 shrink-0 text-muted-foreground" />
          <span className="w-16 shrink-0 text-[10px] text-muted-foreground">{cr.label}</span>
          <CritBar value={item.sub[cr.key]} color={cr.color} />
          <span className="w-7 shrink-0 text-right text-[10px] tabular text-muted-foreground">{item.sub[cr.key]}</span>
        </div>
      ))}
    </div>

    {item.reasons.length > 0 && (
      <div className="mt-2 flex flex-wrap gap-1">
        {item.reasons.map((r) => (
          <span key={r} className="inline-flex items-center gap-1 rounded-md bg-[color:var(--tenant-accent-050)] px-1.5 py-0.5 text-[10px] font-medium accent-text">
            <Sparkles className="h-2.5 w-2.5" />{r}
          </span>
        ))}
      </div>
    )}
  </button>
);

export const ProspectionView = ({ body }) => {
  const [canal, setCanal] = useState('porte_a_porte');
  const [dept, setDept] = useState('all');
  const [selected, setSelected] = useState(null);

  const reqBody = useMemo(
    () => ({ ...body, canal, dept: dept === 'all' ? null : dept, limit: 60 }),
    [body, canal, dept]
  );
  const { data, isLoading, isError, refetch } = usePost('/admin/geo/prospection', reqBody);

  if (isLoading) return <CardSkeleton height="h-[540px]" />;
  if (isError) return <ErrorState body="Impossible de charger les suggestions de prospection." onRetry={refetch} />;

  const depts = data?.departments || [];
  const deptNames = data?.department_names || {};
  const suggestions = (data?.suggestions || []).map((s) => ({ ...s, dept_name: deptNames[s.dept] || s.dept }));

  return (
    <div className="space-y-4">
      {/* Method note */}
      <div className="flex items-start gap-2 rounded-2xl border bg-muted/30 p-3 text-xs text-muted-foreground">
        <Compass className="mt-0.5 h-4 w-4 shrink-0 accent-text" />
        <span className="break-words">
          Villes <strong className="text-foreground">non encore travaillées</strong> dans ce canal, au sein de vos{' '}
          <strong className="text-foreground">{depts.length}</strong> départements habituels, classées sur le{' '}
          <strong className="text-foreground">revenu médian</strong>, le <strong className="text-foreground">potentiel de don</strong> (part des foyers imposés + revenus du patrimoine),
          l'<strong className="text-foreground">âge favorable</strong> (actifs & 45-65 ans) et la <strong className="text-foreground">population</strong>.
          Données INSEE / DGFiP.
        </span>
      </div>

      {/* Controls */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <ToggleGroup type="single" value={canal} onValueChange={(v) => { if (v) { setCanal(v); setSelected(null); } }} variant="outline" size="sm" className="rounded-xl bg-card p-1" data-testid="prospect-canal">
          {CANALS.map((c) => (
            <ToggleGroupItem key={c.key} value={c.key} data-testid={`prospect-canal-${c.key}`} className="rounded-lg px-3 text-xs data-[state=on]:accent-bg-100 data-[state=on]:accent-text">
              {c.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>

        <Select value={dept} onValueChange={(v) => { setDept(v); setSelected(null); }}>
          <SelectTrigger className="w-full rounded-xl sm:w-64" data-testid="prospect-dept">
            <SelectValue placeholder="Tous mes départements" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous mes départements ({depts.length})</SelectItem>
            {depts.map((d) => (
              <SelectItem key={d} value={d}>{deptNames[d] || d} ({d})</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {suggestions.length === 0 ? (
        <EmptyState title="Aucune suggestion" body="Aucune ville candidate pour ce canal dans vos départements. Toutes semblent déjà travaillées." />
      ) : (
        <>
          <p className="text-xs text-muted-foreground break-words">
            <strong className="text-foreground">{fmtInt(data.universe_count)}</strong> villes candidates
            {data.excluded_count > 0 && <> · <strong className="text-foreground">{fmtInt(data.excluded_count)}</strong> déjà travaillées (exclues)</>}.
          </p>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
            <div className="xl:col-span-3">
              <ProspectMap cities={suggestions} selected={selected} onSelect={setSelected} />
            </div>
            <div className="xl:col-span-2">
              <SectionCard title="Villes suggérées" subtitle="Classées par score d'opportunité" testid="prospect-list">
                <div className="max-h-[560px] space-y-2 overflow-y-auto pr-1">
                  {suggestions.map((s, i) => (
                    <ProspectCard key={`${s.city}-${s.dept}`} item={s} rank={i + 1} selected={selected === s.city} onClick={() => setSelected(s.city)} />
                  ))}
                </div>
              </SectionCard>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
